import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCompany, createAdminUser } from '../services/api';
import PasswordInput from '../components/PasswordInput';
import QRCode from 'qrcode';

type Step = 1 | 2 | 3;

interface CompanyInfo  { name: string; subdomain: string; }
interface AdminInfo    { user_name: string; employee_code: string; email: string; password: string; }
interface CreatedInfo  { company_id: string; subdomain: string; employee_code: string; password: string; }

export default function CompanyWizardPage() {
  const navigate = useNavigate();
  const [step, setStep]               = useState<Step>(1);
  const [busy, setBusy]               = useState(false);
  const [error, setError]             = useState('');
  const [company, setCompany]         = useState<CompanyInfo>({ name: '', subdomain: '' });
  const [admin, setAdmin]             = useState<AdminInfo>({ user_name: '', employee_code: 'admin', email: '', password: '' });
  const [created, setCreated]         = useState<CreatedInfo | null>(null);
  const [copied, setCopied]           = useState<string | null>(null);
  const [apkQr, setApkQr]             = useState('');
  const [adminQr, setAdminQr]         = useState('');

  // ── ステップ1：会社情報登録 ──────────────────────────────
  async function handleStep1(e: FormEvent) {
    e.preventDefault();
    setError('');
    // サブドメイン自動生成（会社名から）
    const subdomain = company.subdomain || company.name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 30);
    setBusy(true);
    try {
      await createCompany(company.name, subdomain);
      setCompany(c => ({ ...c, subdomain }));
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setBusy(false);
    }
  }

  // ── ステップ2：管理者アカウント作成 ─────────────────────
  async function handleStep2(e: FormEvent) {
    e.preventDefault();
    setError('');
    // company_id を取得するため会社一覧から検索（簡易的に subdomain で照合）
    setBusy(true);
    try {
      // 会社IDの取得（company作成時に返ってきた値を使いたいが、ここではAPIから再取得）
      const { listCompanies } = await import('../services/api');
      const companies = await listCompanies();
      const c = companies.find(x => x.subdomain === company.subdomain);
      if (!c) throw new Error('会社情報が見つかりません。');

      await createAdminUser(c.id, admin);
      setCreated({
        company_id: c.id,
        subdomain: company.subdomain,
        employee_code: admin.employee_code,
        password: admin.password,
      });
      setStep(3);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setBusy(false);
    }
  }

  // ── コピー機能 ───────────────────────────────────────────
  function copyText(text: string, key: string) {
    void navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }


  useEffect(() => {
    if (!created) return;
    const apkUrl   = `https://dl.unison-sea.com/apk/catms-${created.subdomain}.apk`;
    const loginUrl = `https://${created.subdomain}.${adminDomain}`;
    QRCode.toDataURL(apkUrl,   { width: 200, margin: 2 }).then(setApkQr);
    QRCode.toDataURL(loginUrl, { width: 200, margin: 2 }).then(setAdminQr);
  }, [created]);
  const adminDomain = 'unison-sea.com';
  const adminUrl  = created ? `https://${created.subdomain}.${adminDomain}` : '';
  const loginInfo = created
    ? `ログインURL: ${adminUrl}\n社員番号: ${created.employee_code}\nパスワード: ${created.password}`
    : '';

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>

      {/* 戻るボタン */}
      <button onClick={() => navigate('/')} style={backBtnStyle}>← 会社一覧に戻る</button>

      {/* タイトル */}
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: '16px 0 8px' }}>新しい会社を追加</h1>

      {/* ステップインジケーター */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 32, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        {([
          { n: 1, label: '会社情報' },
          { n: 2, label: '管理者アカウント' },
          { n: 3, label: 'APK手順' },
        ] as const).map(({ n, label }) => (
          <div key={n} style={{
            flex: 1, textAlign: 'center', padding: '10px 0', fontSize: 13, fontWeight: 600,
            background: step === n ? '#1e40af' : step > n ? '#dbeafe' : '#f8fafc',
            color: step === n ? '#fff' : step > n ? '#1d4ed8' : '#94a3b8',
            borderRight: n < 3 ? '1px solid #e2e8f0' : undefined,
          }}>
            {step > n ? '✓ ' : `${n}. `}{label}
          </div>
        ))}
      </div>

      {/* エラー */}
      {error && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>{error}</div>}

      {/* ── ステップ1 ── */}
      {step === 1 && (
        <form onSubmit={handleStep1}>
          <div style={cardStyle}>
            <h2 style={sectionTitleStyle}>🏢 会社情報を入力してください</h2>

            <label style={labelStyle}>会社名 <span style={{ color: '#ef4444' }}>*</span></label>
            <input
              value={company.name}
              onChange={e => setCompany(c => ({
                ...c, name: e.target.value,
                subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 30)
              }))}
              placeholder="例：大和汽船株式会社" required style={inputStyle}
            />
            <div style={hintStyle}>正式な会社名を入力してください</div>

            <label style={{ ...labelStyle, marginTop: 20 }}>管理画面URL（サブドメイン）<span style={{ color: '#ef4444' }}>*</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1.5px solid #d1d5db', borderRadius: 8, overflow: 'hidden' }}>
              <input
                value={company.subdomain}
                onChange={e => setCompany(c => ({ ...c, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                placeholder="yamato-kisen" required
                style={{ ...inputStyle, border: 'none', borderRadius: 0, flex: 1, outline: 'none' }}
              />
              <span style={{ background: '#f1f5f9', padding: '0 12px', color: '#64748b', fontSize: 13, whiteSpace: 'nowrap', alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}>.unison-sea.com</span>
            </div>
            <div style={hintStyle}>管理者はこのURLからログインします（小文字英数字・ハイフンのみ）</div>
            {company.subdomain && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13, color: '#15803d' }}>
                🔗 管理画面URL：<strong>https://{company.subdomain}.unison-sea.com</strong>
              </div>
            )}
          </div>

          <button type="submit" disabled={busy || !company.name || !company.subdomain} style={{ ...submitBtnStyle, opacity: busy || !company.name || !company.subdomain ? 0.5 : 1 }}>
            {busy ? '登録中...' : '次へ：管理者アカウントを作成 →'}
          </button>
        </form>
      )}

      {/* ── ステップ2 ── */}
      {step === 2 && (
        <form onSubmit={handleStep2}>
          <div style={cardStyle}>
            <h2 style={sectionTitleStyle}>👤 管理者アカウントを作成してください</h2>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#1e40af' }}>
              会社 <strong>{company.name}</strong> の管理者アカウントを作成します
            </div>

            <label style={labelStyle}>担当者名 <span style={{ color: '#ef4444' }}>*</span></label>
            <input value={admin.user_name} onChange={e => setAdmin(a => ({ ...a, user_name: e.target.value }))}
              placeholder="例：山田 花子" required style={inputStyle} />

            <label style={{ ...labelStyle, marginTop: 16 }}>社員番号・ログインID <span style={{ color: '#ef4444' }}>*</span></label>
            <input value={admin.employee_code} onChange={e => setAdmin(a => ({ ...a, employee_code: e.target.value }))}
              placeholder="例：admin" required style={inputStyle} />
            <div style={hintStyle}>管理者がログイン時に使用するIDです</div>

            <label style={{ ...labelStyle, marginTop: 16 }}>メールアドレス</label>
            <input type="email" name="new-company-admin-email" autoComplete="off" value={admin.email} onChange={e => setAdmin(a => ({ ...a, email: e.target.value }))}
              placeholder="例：admin@yamato.co.jp" style={inputStyle} />

            <label style={{ ...labelStyle, marginTop: 16 }}>初期パスワード <span style={{ color: '#ef4444' }}>*</span></label>
            <PasswordInput name="new-company-admin-password" autoComplete="new-password" value={admin.password} onChange={e => setAdmin(a => ({ ...a, password: e.target.value }))}
              placeholder="例：Yamato@2026" required style={inputStyle} />
            <div style={hintStyle}>⚠️ 初回ログイン後にパスワードを変更するよう案内してください</div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <button type="button" onClick={() => setStep(1)} style={backSubmitBtnStyle}>← 戻る</button>
            <button type="submit" disabled={busy} style={{ ...submitBtnStyle, flex: 1, opacity: busy ? 0.5 : 1 }}>
              {busy ? '作成中...' : 'アカウントを作成 →'}
            </button>
          </div>
        </form>
      )}

      {/* ── ステップ3：APKビルド手順 ── */}
      {step === 3 && created && (
        <div>
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <h2 style={sectionTitleStyle}>✅ 登録完了！次の手順に進んでください</h2>

            {/* チェックリスト */}
            <div style={{ marginBottom: 20 }}>
              {[
                { done: true,  label: 'DBに会社を登録した' },
                { done: true,  label: '管理者アカウントを作成した' },
                { done: true,  label: '管理サイト・船員アプリのドメインを自動整備した' },
                { done: false, label: '会社詳細で「APKをビルド」を押す' },
                { done: false, label: 'APKのQR/URLとログイン情報を船会社に案内する' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: 18 }}>{item.done ? '✅' : '☐'}</span>
                  <span style={{ fontSize: 14, color: item.done ? '#16a34a' : '#374151', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* QRコード */}
          <div style={{ ...cardStyle, marginTop: 16 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#1e293b' }}>📲 QRコード</h3>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>

              {/* APKダウンロードQR */}
              <div style={{ flex: 1, minWidth: 180, textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>📱 アプリ（APK）インストール</div>
                {apkQr
                  ? <img src={apkQr} alt="APK QR" style={{ width: 180, height: 180, border: '1px solid #e2e8f0', borderRadius: 8 }} />
                  : <div style={{ width: 180, height: 180, background: '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#94a3b8', margin: '0 auto' }}>生成中...</div>
                }
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>スキャン → APKダウンロード → インストール</div>
                <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>※ APKビルド後に有効になります</div>
              </div>

              {/* 管理画面ログインQR */}
              <div style={{ flex: 1, minWidth: 180, textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>🖥️ 管理画面ログイン</div>
                {adminQr
                  ? <img src={adminQr} alt="Admin QR" style={{ width: 180, height: 180, border: '1px solid #e2e8f0', borderRadius: 8 }} />
                  : <div style={{ width: 180, height: 180, background: '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#94a3b8', margin: '0 auto' }}>生成中...</div>
                }
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>スキャン → 管理者ログイン画面へ</div>
              </div>
            </div>
          </div>

          {/* APKビルド案内（ポータル内で完結） */}
          <div style={{ ...cardStyle, marginTop: 16 }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 700, color: '#1e293b' }}>📱 船員アプリ（APK）の作成</h3>
            <div style={hintStyle}>
              エンジニアへの依頼は不要です。<b>会社一覧 → この会社の「管理」または詳細を開き、「🔨 APKをビルド」ボタン</b>を押すと、
              この会社専用の署名済みAPKが自動でビルド・配信されます（数分）。完了後、上のQR／ダウンロードURLがそのまま使えます。
            </div>
          </div>

          {/* 管理者ログイン情報 */}
          <div style={{ ...cardStyle, marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>🔑 船会社へ案内するログイン情報</h3>
              <button onClick={() => copyText(loginInfo, 'login')} style={copyBtnStyle}>
                {copied === 'login' ? '✓ コピー済み' : 'コピー'}
              </button>
            </div>
            <pre style={codeStyle}>{loginInfo}</pre>
            <div style={hintStyle}>この情報を船会社の担当者にメール等で送ってください。</div>
          </div>

          <button onClick={() => navigate('/')} style={{ ...submitBtnStyle, marginTop: 20, background: '#16a34a' }}>
            ✓ 完了 — 会社一覧に戻る
          </button>
        </div>
      )}
    </div>
  );
}

const cardStyle: React.CSSProperties        = { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 0 };
const sectionTitleStyle: React.CSSProperties = { fontSize: 16, fontWeight: 700, color: '#1e293b', margin: '0 0 20px' };
const labelStyle: React.CSSProperties       = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle: React.CSSProperties       = { width: '100%', padding: '10px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' };
const hintStyle: React.CSSProperties        = { fontSize: 12, color: '#94a3b8', marginTop: 5 };
const submitBtnStyle: React.CSSProperties   = { width: '100%', padding: '13px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 16 };
const backSubmitBtnStyle: React.CSSProperties = { padding: '13px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 16 };
const backBtnStyle: React.CSSProperties     = { background: 'none', border: 'none', color: '#64748b', fontSize: 14, cursor: 'pointer', padding: '4px 0' };
const copyBtnStyle: React.CSSProperties     = { background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151' };
const codeStyle: React.CSSProperties        = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 14px', fontSize: 13, fontFamily: 'monospace', margin: 0, whiteSpace: 'pre-wrap', color: '#1e293b' };
